import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { TranslationProvider } from "@/contexts/TranslationContext";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import CookieConsent from "./components/CookieConsent";
import SeoHead from "./components/SeoHead";
import AutoTranslate from "./components/AutoTranslate";

// Lazy load non-critical routes
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const BlogList = lazy(() => import("./pages/admin/BlogList"));
const BlogEditor = lazy(() => import("./pages/admin/BlogEditor"));
const BlogGenerator = lazy(() => import("./pages/admin/BlogGenerator"));
const MediaLibrary = lazy(() => import("./pages/admin/MediaLibrary"));
const SiteContent = lazy(() => import("./pages/admin/SiteContent"));
const Submissions = lazy(() => import("./pages/admin/Submissions"));
const PagesList = lazy(() => import("./pages/admin/PagesList"));
const PageEditor = lazy(() => import("./pages/admin/PageEditor"));
const SeoAnalytics = lazy(() => import("./pages/admin/SeoAnalytics"));
const NumiconPage = lazy(() => import("./pages/programs/NumiconPage"));
const MentalnaAritmetikaPage = lazy(() => import("./pages/programs/MentalnaAritmetikaPage"));
const ProgrammierenPage = lazy(() => import("./pages/programs/ProgrammierenPage"));
const KleinerAlleswisserPage = lazy(() => import("./pages/programs/KleinerAlleswisserPage"));
const DynamicPage = lazy(() => import("./pages/DynamicPage"));
const ResearchPage = lazy(() => import("./pages/ResearchPage"));

const queryClient = new QueryClient();

const LoadingFallback = () => <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <TranslationProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <CookieConsent />
            <SeoHead />
            <AutoTranslate />
            <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/programi/numicon" element={<NumiconPage />} />
              <Route path="/programi/mentalna-aritmetika" element={<MentalnaAritmetikaPage />} />
              <Route path="/programi/programmieren" element={<ProgrammierenPage />} />
              <Route path="/programi/kleiner-alleswisser" element={<KleinerAlleswisserPage />} />
              <Route path="/istrazivanja" element={<ResearchPage />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="blog" element={<BlogList />} />
                <Route path="blog/new" element={<BlogEditor />} />
                <Route path="blog/generator" element={<BlogGenerator />} />
                <Route path="blog/:id" element={<BlogEditor />} />
                <Route path="content" element={<SiteContent />} />
                <Route path="media" element={<MediaLibrary />} />
                <Route path="submissions" element={<Submissions />} />
                <Route path="pages" element={<PagesList />} />
                <Route path="pages/new" element={<PageEditor />} />
                <Route path="pages/:id" element={<PageEditor />} />
                <Route path="seo" element={<SeoAnalytics />} />
              </Route>
              {/* Redirekcije za uklonjene Blog rute (301-Verhalten) */}
              <Route path="/blog" element={<Navigate to="/" replace />} />
              <Route path="/blog/*" element={<Navigate to="/" replace />} />
              <Route path="/blogs" element={<Navigate to="/" replace />} />
              <Route path="/blogs/*" element={<Navigate to="/" replace />} />
              <Route path="/stranica/*" element={<DynamicPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
          </TranslationProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
